import React from "react";
import { Box } from "@mui/material";
import {
  codeBandsFromCatalogSubs,
  codeBandsFromDigits,
  formatCodeBandRange,
} from "../shared/eventCodeRange";

const CODE_HINT_SX = {
  color: "text.secondary",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.75rem",
  lineHeight: 1.45,
} as const;

export type CodeBandVisibility = "rising" | "resolving" | "both";

export type ExpectedCodeBandsHintProps = {
  baseDigit: number;
  /** Single sub band (base + sub digit pair). */
  subDigit?: number;
  /** Catalog sub digits for this base — used for base-level range when subDigit is omitted. */
  catalogSubDigits?: number[];
  /** Which band(s) to show; default both (e.g. sub-function tiles in the creator). */
  show?: CodeBandVisibility;
  /** Inline: rising and resolving on one row (footer strip). */
  layout?: "stack" | "inline";
  sx?: object;
};

/** Compact rising / resolving code band lines for a base+sub digit pair. */
export default function ExpectedCodeBandsHint({
  baseDigit,
  subDigit,
  catalogSubDigits,
  show = "both",
  layout = "stack",
  sx,
}: ExpectedCodeBandsHintProps) {
  const bands =
    subDigit != null
      ? codeBandsFromDigits(baseDigit, subDigit)
      : codeBandsFromCatalogSubs(baseDigit, catalogSubDigits ?? []);
  const showRising = show === "rising" || show === "both";
  const showResolving = show === "resolving" || show === "both";
  const inline = layout === "inline";

  return (
    <Box
      sx={{
        ...CODE_HINT_SX,
        ...(inline ? { display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 1.25 } : {}),
        ...sx,
      }}
      component={inline ? "span" : "div"}
    >
      {showRising && (
        <Box component="span" display="inline" title="Rising (0xxxx)" sx={{ whiteSpace: "nowrap" }}>
          ↑ {formatCodeBandRange(bands.risingMin, bands.risingMax)}
        </Box>
      )}
      {showResolving && (
        <Box
          component="span"
          display="inline"
          title="Resolving (1xxxx)"
          sx={{ whiteSpace: "nowrap" }}
        >
          ↓ {formatCodeBandRange(bands.resolvingMin, bands.resolvingMax)}
        </Box>
      )}
    </Box>
  );
}

/** Shared UI label for rising/resolving code interval hints. */
export const EVENT_CODE_RANGE_LABEL = "Event code range";

export function expectedCodeBandLabel(_show?: CodeBandVisibility): string {
  return EVENT_CODE_RANGE_LABEL;
}
